import { useState, useEffect } from 'react';
import { HelpCircle, X, ChevronRight, ExternalLink } from 'lucide-react';
import { GUIDES } from '../data/helpGuides';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SafeText } from './SafeText';

/**
 * Floating "?" bubble that opens a slide-over drawer with a relevant guide.
 *
 * Props:
 *   guideId: string — id from helpGuides.GUIDES (e.g. 'corrections', 'suppliers')
 *   position: 'br' | 'tr' (default 'br') — bottom-right or top-right
 */
export const ContextualHelp = ({ guideId, position = 'br' }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  const guide = GUIDES.find(g => g.id === guideId);
  if (!guide) return null;

  const positionClass = position === 'tr'
    ? 'top-4 right-4'
    : 'bottom-6 right-6';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`fixed ${positionClass} z-40 w-11 h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center group`}
        title={`Help: ${guide.title}`}
        data-testid={`contextual-help-bubble-${guideId}`}
        aria-label={`Open help for ${guide.title}`}
      >
        <HelpCircle size={20} />
        <span className="absolute right-full mr-3 whitespace-nowrap bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Help
        </span>
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 transition-opacity"
            onClick={() => setOpen(false)}
            data-testid={`contextual-help-backdrop-${guideId}`}
          />
          <aside
            className="fixed top-0 right-0 z-50 h-full w-full sm:w-[480px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
            data-testid={`contextual-help-drawer-${guideId}`}
            role="dialog"
            aria-modal="true"
          >
            <div className={`p-5 border-b border-slate-100 bg-gradient-to-r ${guide.color} text-white`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide opacity-90">Help</p>
                  <h2 className="text-xl font-heading font-bold">{guide.title}</h2>
                  <p className="text-xs opacity-90 mt-1">{guide.steps.length} steps</p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-md hover:bg-white/20 transition-colors"
                  data-testid={`contextual-help-close-${guideId}`}
                  aria-label="Close help"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {guide.steps.map((s, i) => (
                <div key={i} className="border-l-2 border-slate-200 pl-4">
                  <h3 className="text-sm font-heading font-semibold text-slate-900 mb-1.5">{s.title}</h3>
                  <SafeText text={s.body} className="text-sm text-slate-600 leading-relaxed" />
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <p className="text-xs text-slate-500">Press <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-xs">Esc</kbd> to close</p>
              <a
                href="#help"
                onClick={() => setOpen(false)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                data-testid={`contextual-help-open-full-${guideId}`}
              >
                Full Help centre <ExternalLink size={11} />
              </a>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

/**
 * Inline help chip — a smaller variant that sits next to a heading rather than
 * floating. Useful for embedding beside a section title.
 */export const InlineHelpChip = ({ guideId, label }) => {
  const [open, setOpen] = useState(false);
  const guide = GUIDES.find(g => g.id === guideId);
  if (!guide) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
        data-testid={`inline-help-chip-${guideId}`}
      >
        <HelpCircle size={12} />
        {label || `How does ${guide.title} work?`}
        <ChevronRight size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} />
          <aside className="fixed top-0 right-0 z-50 h-full w-full sm:w-[480px] bg-white shadow-2xl flex flex-col" role="dialog" aria-modal="true">
            <div className={`p-5 border-b border-slate-100 bg-gradient-to-r ${guide.color} text-white flex items-start justify-between`}>
              <div>
                <p className="text-xs uppercase tracking-wide opacity-90">Help</p>
                <h2 className="text-xl font-heading font-bold">{guide.title}</h2>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-white/20"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {guide.steps.map((s, i) => (
                <div key={i} className="border-l-2 border-slate-200 pl-4">
                  <h3 className="text-sm font-heading font-semibold text-slate-900 mb-1.5">{s.title}</h3>
                  <SafeText text={s.body} className="text-sm text-slate-600 leading-relaxed" />
                </div>
              ))}
            </div>
          </aside>
        </>
      )}
    </>
  );
};


/**
 * HelpTip — a tiny "?" icon that shows a short tooltip on hover / focus.
 * Ideal for annotating individual form fields, buttons, or table headers.
 *
 * Props:
 *   text: string  — the tooltip text (short — 1-3 sentences)
 *   testId?: string — optional data-testid suffix
 *   side?: 'top'|'right'|'bottom'|'left' — tooltip position (default 'top')
 */
export const HelpTip = ({ text, testId, side = 'top' }) => {
  if (!text) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Help"
            data-testid={testId ? `helptip-${testId}` : 'helptip'}
            className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors align-middle"
            onClick={(e) => e.preventDefault()}
          >
            <HelpCircle size={13} />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
